import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { useCustomerUserRelationCreate } from "gen/endpoints/customer-user-relation/customer-user-relation";
import {
  getInternalProvisioningCustomerUserRelationsTableDataRetrieveQueryKey,
  getInternalUnrelatedUserListQueryKey,
  useInternalUnrelatedUserList,
} from "gen/endpoints/internal/internal";
import { CustomerUserRelationCreateAPIDataRequest } from "gen/model/customerUserRelationCreateAPIDataRequest";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form from "shared/components/form/Form";
import { FormSelect } from "shared/components/input/select/Select";
import { SelectOption } from "shared/components/input/select/SelectOption";

export interface InternalAddCustomerUserRelationModalProps {
  customerId: string;
  onCloseComplete(): void;
}

export const InternalAddCustomerUserRelationModal: React.FC<
  InternalAddCustomerUserRelationModalProps
> = ({ customerId, onCloseComplete }) => {
  const { t } = useTranslation(["internal", "auth", "common"]);
  const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true });
  const { mutateAsync: createCustomerUserRelation } =
    useCustomerUserRelationCreate();

  const form = useForm<CustomerUserRelationCreateAPIDataRequest>({
    defaultValues: {
      customer_id: customerId,
    },
  });
  const customer_id = useWatch({ control: form.control, name: "customer_id" });
  const queryClient = useQueryClient();

  const { data: customerUserList } = useInternalUnrelatedUserList(
    { customer_id },
    { query: { enabled: !!customer_id } },
  );

  const onSubmit = async (data: CustomerUserRelationCreateAPIDataRequest) => {
    await createCustomerUserRelation({ data });
    await Promise.allSettled([
      queryClient.invalidateQueries({
        queryKey:
          getInternalProvisioningCustomerUserRelationsTableDataRetrieveQueryKey(
            customerId,
          ),
      }),
      queryClient.invalidateQueries({
        queryKey: getInternalUnrelatedUserListQueryKey({
          customer_id: customerId,
        }),
      }),
    ]);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onCloseComplete={onCloseComplete}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <Form
          form={form}
          onSubmit={onSubmit}
          onSuccess={onClose}
          successMessage={t("internal:modal.linkUser.success")}
        >
          <ModalHeader>
            {t("internal:modal.linkUser.title")}
            <ModalCloseButton />
          </ModalHeader>
          <ModalBody>
            <FormSelect title={t("common:user")} name="user_email">
              {customerUserList?.map(({ email }) => (
                <SelectOption key={`user_${email}`} value={email}>
                  {email}
                </SelectOption>
              ))}
            </FormSelect>
          </ModalBody>
          <ModalFooter gap={2}>
            <ButtonOrder
              dismiss={<FormCancelButton onClick={onClose} autoFocus={true} />}
              confirm={<FormSubmitButton>{t("common:save")}</FormSubmitButton>}
            />
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  );
};
